import type { HistoryEntry } from "./session.ts";

/** The last prepared decision we can actually take back to, in this course. */
export function courseReturnPoint(history: HistoryEntry[]) {
  const entry = [...history]
    .reverse()
    .find((move) => move.byStudent && !move.setup && move.courseSan && move.san !== move.courseSan);
  return entry
    ? { ply: entry.ply, moveNumber: Math.ceil(entry.ply / 2), san: entry.courseSan! }
    : null;
}

export type CourseDeparture = NonNullable<ReturnType<typeof courseReturnPoint>> & {
  playedSan: string;
  routeName: string | null;
  lessons: string[];
  /**
   * How the book stands on the move that left the course. Only a road the
   * book recognizes, and that is not a tactical error, may be called sound.
   */
  standing: "sound" | "warned" | "outside";
};

/** The departure the coach can name, plus any lessons that already teach it. */
export function courseDeparture(
  history: HistoryEntry[],
  lessons: string[]
): CourseDeparture | null {
  const point = courseReturnPoint(history);
  if (!point) return null;
  const played = history.find((entry) => entry.ply === point.ply);
  const kind = played?.openingPath?.kind;
  const standing =
    kind === "warning" || played?.qualityBand === "tactical-error"
      ? "warned"
      : kind === "course" || kind === "transposition" || kind === "variation"
        ? "sound"
        : "outside";
  return {
    ...point,
    playedSan: played?.san || "",
    // A name only describes a road the book recognizes.
    routeName: standing === "sound" ? (played?.openingPath?.name ?? null) : null,
    lessons,
    standing,
  };
}
